import { api } from "@/lib/api";

import type {
  CreateExpenseReq,
  Expense,
  ExpenseCategory,
  ExpenseCostImpactPreview,
  ExpenseList,
  ExpenseStatus,
  SalePaymentMethod,
  UpdateExpenseReq,
} from "@artisancode/api-types";

export interface ExpenseListParams {
  id?: string;
  status?: ExpenseStatus;
  page?: number;
  per_page?: number;
  q?: string;
  category?: ExpenseCategory;
  payment_method?: SalePaymentMethod;
  date_from?: string;
  date_to?: string;
  workspace_id?: string;
}

export const expenseService = {
  list: (params?: ExpenseListParams) =>
    api.get<ExpenseList>(
      "/expenses",
      params as Record<string, string | number | boolean | undefined>,
    ),

  /** Single expense (with items and receipts) — the list endpoint filtered by id. */
  getById: async (id: string): Promise<Expense | null> => {
    const res = await api.get<ExpenseList>("/expenses", { id, per_page: 1 });
    return res.items[0] ?? null;
  },

  complete: (id: string, body?: { acknowledgeCostImpact?: boolean }) =>
    api.post<Expense>(`/expenses/${id}/complete`, body ?? {}),

  previewCost: (body: CreateExpenseReq) =>
    api.post<ExpenseCostImpactPreview>("/expenses/preview-cost", body),

  previewDraftCost: (id: string) =>
    api.get<ExpenseCostImpactPreview>(`/expenses/${id}/preview-cost`),

  create: (body: CreateExpenseReq, idempotencyKey?: string) =>
    api.post<Expense>(
      "/expenses",
      body,
      idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    ),

  update: (id: string, body: UpdateExpenseReq) =>
    api.put<Expense>(`/expenses/${id}`, body),

  delete: (id: string, reason: string) =>
    api.del(`/expenses/${id}`, { reason }),
};
